import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import ConfigContext from '../contexts/ConfigContext';
import { useGetCmsPageForPageQuery } from '../redux/slices/cmsPageSlice';
import { sanitizeHtml } from '../modules/formatUtils';

/*
 * usePageContent — fetches CMS marketing copy for a given page slug, scoped
 * to the city this Rails instance is running. Falls back to `null` content
 * when no published row exists, so consumers render hardcoded JSX gracefully.
 *
 * Templates support {{city_name}}, {{vehicle_type}}, etc. placeholders that
 * are replaced with values from ConfigContext (the city YAML config).
 *
 * SSR strategy: PagesController pre-fetches all published CmsPage rows for
 * the current city and injects them into ConfigContext via `cms_pages` keyed
 * by page_slug. We read from that synchronously first, so the Node SSR
 * renderer produces HTML with bespoke CMS copy baked in — which is what
 * Google crawls, avoiding duplicate-content penalties.
 *
 * RTK Query is only used as a fallback when the context is missing the row,
 * which primarily happens during admin-side React renders where ConfigContext
 * may not have been seeded with pre-fetched rows.
 *
 * Usage:
 *   const { content, isLoading } = usePageContent('home');
 *   <h1>{content?.hero?.headline || `Default Headline for ${city_name}`}</h1>
 *
 *   // For richtext fields produced by the CMS Draft.js editor:
 *   <CmsRichText html={content?.hero?.body} fallback={<p>Default body</p>} />
 */

const TOKEN_RE = /\{\{\s*(\w+)\s*\}\}/g;

function interpolate(value, vars) {
  if (typeof value === 'string') {
    return value.replace(TOKEN_RE, (_, key) => {
      const replacement = vars[key];
      return replacement != null ? String(replacement) : '';
    });
  }
  if (Array.isArray(value)) {
    return value.map((v) => interpolate(v, vars));
  }
  if (value && typeof value === 'object') {
    const out = {};
    for (const k of Object.keys(value)) {
      out[k] = interpolate(value[k], vars);
    }
    return out;
  }
  return value;
}

function usePageContent(pageSlug) {
  // Memoize the `|| {}` fallback so the useMemo below doesn't see a fresh
  // object identity on every render — react-hooks/exhaustive-deps would
  // otherwise complain that `config` could change every render.
  const rawConfig = useContext(ConfigContext);
  const config = useMemo(() => rawConfig || {}, [rawConfig]);

  // Server-rendered row from ConfigContext — this is populated by
  // PagesController and available during SSR, so the hook resolves
  // synchronously and the server-rendered HTML contains CMS copy.
  const prefetched = pageSlug && config.cms_pages ? config.cms_pages[pageSlug] : null;

  // Fall back to RTK Query only when there's no pre-fetched row. We skip the
  // fetch entirely if ConfigContext was seeded (meaning Rails already told us
  // which rows exist for this city — no row = intentional, fallback to JSX).
  const hasCmsPagesContext = config && config.cms_pages != null;
  const { data, isLoading, isError } = useGetCmsPageForPageQuery(pageSlug, {
    skip: !pageSlug || hasCmsPagesContext,
  });

  const content = useMemo(() => {
    const source = prefetched || data;
    if (!source || !source.content) return null;
    const vars = {
      city_name: config.city_name,
      state: config.state,
      site_title: config.site_title,
      site_title_spaces: config.site_title_spaces,
      base_url: config.base_url,
      vehicle_type: config.vehicle_type,
      vehicle_type_lower: typeof config.vehicle_type === 'string'
        ? config.vehicle_type.toLowerCase()
        : config.vehicle_type,
    };
    return interpolate(source.content, vars);
  }, [prefetched, data, config]);

  return { content, isLoading, isError };
}

/*
 * CmsRichText — render an HTML string from a CMS richtext field.
 *
 * Sanitizes via DOMPurify (formatUtils.sanitizeHtml) before injecting through
 * dangerouslySetInnerHTML so a malicious admin (or a compromised CMS row)
 * can't inject script tags.
 *
 * If `html` is empty/null, renders the `fallback` instead — keeps every
 * page-component callsite a one-liner with graceful degradation:
 *
 *   <CmsRichText html={content?.hero?.body} fallback={<p>Default copy</p>} />
 *
 * The wrapping div uses className `CmsRichText` so styles can target the
 * generated headings / paragraphs / lists without polluting global CSS.
 */
function CmsRichText({ html, fallback, className, tag: Tag }) {
  if (!html || typeof html !== 'string' || html.trim() === '' || html.trim() === '<p></p>') {
    return fallback;
  }
  const safe = sanitizeHtml(html);
  if (!safe) return fallback;
  // eslint-disable-next-line react/no-danger
  return <Tag className={className} dangerouslySetInnerHTML={{ __html: safe }} />;
}

CmsRichText.propTypes = {
  html: PropTypes.string,
  fallback: PropTypes.node,
  className: PropTypes.string,
  tag: PropTypes.string,
};

CmsRichText.defaultProps = {
  html: null,
  fallback: null,
  className: 'CmsRichText',
  tag: 'div',
};

export default usePageContent;
export { interpolate, CmsRichText };
